import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, LinearProgress, Link, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { apiService, CatalystData } from '../services/api';

const STAGE_COLOR: Record<string, string> = {
  Confirmed: '#22C55E', Forming: '#38BDF8', Late: '#F59E0B', Fading: '#94A3B8', Watch: '#64748B',
};
const STAGE_ORDER = ['Confirmed', 'Forming', 'Late', 'Fading', 'Watch'];
const pct = (v?: number | null) => (v == null ? '–' : `${Math.round(v * 100)}%`);

export default function CatalystChains() {
  const [data, setData] = useState<CatalystData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showWatch, setShowWatch] = useState(false);
  const [drivers, setDrivers] = useState<any>(null);
  useEffect(() => { apiService.getCatalysts().then(setData).catch(e => setError(e?.message || 'Failed to load')); }, []);
  useEffect(() => { apiService.getCatalystDrivers().then(setDrivers).catch(() => setDrivers(null)); }, []);
  const runNow = () => {
    setBusy(true);
    apiService.runCatalysts().then(setData).catch(e => setError(e?.response?.data?.detail || 'Run failed')).finally(() => setBusy(false));
  };
  const pa = data?.phase_a;
  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={700}>Catalyst Chains</Typography>
        <Button variant="outlined" onClick={runNow} disabled={busy}>{busy ? <CircularProgress size={18} /> : 'Run now'}</Button>
      </Box>
      <Alert severity="warning" sx={{ my: 2 }}>
        <strong>Shadow mode — under evaluation, not a recommendation.</strong> A need in the news → the products that
        bottleneck it → the listed companies that make them → each company's own evidence (8-K agreements, record backlog,
        raised guidance) → a stage. Every evening's result is frozen and scored later on the Scoreboard; nothing here is
        shown as a pick until it has beaten its yardsticks over 3–6 months.
        {data?.snapshot_days != null && ` ${data.snapshot_days} day(s) frozen so far.`}
      </Alert>
      {pa && (
        <Alert severity={pa.pass?.length ? 'success' : 'info'} sx={{ mb: 2 }}>
          <strong>History test of the evidence (8-Ks 2017–2026, {pa.events?.toLocaleString()} events):</strong>{' '}
          {Object.entries(pa.rules || {}).map(([k, r]: any) => `${k}: ${r.exploder_pct}% became top-10% movers vs ${r.theme_only_exploder_pct}% for the group alone`).join('; ')}.
          {' '}{pa.pass?.length ? `Passed: ${pa.pass.join(', ')}.` : 'It did not pass the pre-set bar, so evidence is shown as context and gates which stocks appear, but does not rank them on its own.'}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress />}
      {data && !data.as_of && <Alert severity="info">No snapshot yet. It runs every weekday at 17:40 ET, or press "Run now".</Alert>}
      <Grid container spacing={2}>
        {data?.needs.map(n => {
          const cands = n.candidates.filter(c => showWatch || c.stage !== 'Watch')
            .sort((a, b) => STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage) || b.score - a.score);
          return (
            <Grid item xs={12} lg={6} key={n.need_id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight={700}>{n.name}</Typography>
                    {n.kind && <Chip size="small" label={n.kind.replace('_', ' ')} />}
                    <Chip size="small" label={n.strength > 0 ? 'rising in the news' : 'quieter than usual'}
                      color={n.strength > 0 ? 'success' : 'default'} variant="outlined" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {n.articles_7d} articles in 7 days from {n.outlets_7d} outlets (usual week {n.weekly_median?.toFixed(0)}) ·{' '}
                    {n.members} companies make its products
                  </Typography>
                  <Tooltip title="Share of the need's companies beating SPY over 3 months; 60% marks a moving group (Feature 35)">
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption">Group beating SPY: {pct(n.breadth)} (moving at 60%)</Typography>
                      <LinearProgress variant="determinate" value={Math.min(100, (n.breadth ?? 0) * 100)}
                        color={(n.breadth ?? 0) >= 0.6 ? 'success' : 'primary'} />
                    </Box>
                  </Tooltip>
                  {(n.headlines || []).slice(0, 3).map((h: any, i: number) => (
                    <Typography key={i} variant="caption" display="block" noWrap>
                      • <Link href={h.url} target="_blank" rel="noopener">{h.title}</Link> <span style={{ opacity: 0.6 }}>({h.source})</span>
                    </Typography>
                  ))}
                  <Table size="small" sx={{ mt: 1, '& td, & th': { px: 0.75 } }}>
                    <TableHead>
                      <TableRow><TableCell>Stock</TableCell><TableCell>Stage</TableCell><TableCell align="right">6 mo</TableCell><TableCell>Evidence (30 days)</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {cands.slice(0, 12).map(c => (
                        <TableRow key={c.symbol}>
                          <TableCell sx={{ fontWeight: 600 }}>{c.symbol}</TableCell>
                          <TableCell><Chip size="small" label={c.stage} sx={{ height: 18, bgcolor: `${STAGE_COLOR[c.stage]}33` }} /></TableCell>
                          <TableCell align="right">{c.ret_6m != null ? `${c.ret_6m > 0 ? '+' : ''}${Math.round(c.ret_6m * 100)}%` : '–'}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            {(c.evidence || []).slice(0, 2).map((e: any, i: number) => (
                              <Typography key={i} variant="caption" display="block" noWrap>
                                {e.kind === 'news' ? <Link href={e.url} target="_blank" rel="noopener">{e.title}</Link>
                                  : <Link href={e.url} target="_blank" rel="noopener">8-K material agreement {e.date}</Link>}
                              </Typography>
                            ))}
                            {!c.evidence_count && <Typography variant="caption" color="text.disabled">none yet</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!cands.length && <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No candidate with evidence.</Typography></TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {drivers && drivers.extracted_total > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700}>What the news is about this week</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Each new article read by a local model ({drivers.model}, on the GPU) for the economic driver it describes —
              shadow mode, recorded since {drivers.first?.slice(0, 10)} ({drivers.extracted_total.toLocaleString()} articles).
              It does not pick stocks; it shows which curated needs the news supports and which new ones it keeps naming.
            </Typography>
            <Grid container spacing={2}>
              {[['Curated needs', drivers.needs], ['Not in the curated list (2+ outlets)', drivers.new]].map(([title, rows]: any) => (
                <Grid item xs={12} md={6} key={title}>
                  <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
                  <Table size="small" sx={{ '& td, & th': { px: 0.75 } }}>
                    <TableHead><TableRow><TableCell>Driver</TableCell><TableCell align="right">Articles</TableCell>
                      <TableCell align="right">Outlets</TableCell><TableCell>Gains</TableCell></TableRow></TableHead>
                    <TableBody>
                      {rows.slice(0, 10).map((g: any) => (
                        <TableRow key={g.key}>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Tooltip title={(g.headlines || []).map((h: any) => h.title).join(' · ')}>
                              <Typography variant="caption" fontWeight={600}>{g.name}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{g.articles}</TableCell>
                          <TableCell align="right">{g.outlets}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}><Typography variant="caption" noWrap display="block">{g.products.join(', ')}</Typography></TableCell>
                        </TableRow>
                      ))}
                      {!rows.length && <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">None this week.</Typography></TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
      {data?.as_of && (
        <Button size="small" sx={{ mt: 2 }} onClick={() => setShowWatch(!showWatch)}>
          {showWatch ? 'Hide' : 'Show'} companies named with a need but without evidence yet
        </Button>
      )}
    </Box>
  );
}
